/**
 * Client-side filtering for the unified campaigns dashboard.
 *
 * Channel classification: live chat is the ongoing type on a web widget
 * inbox, SMS and WhatsApp are one-off types split by the inbox channel.
 *
 * Status filtering builds on `deriveCampaignStatus`. The dashboard's "Live"
 * chip covers both `active` and `processing` — a campaign mid-send is live
 * from the user's point of view.
 */
import { CAMPAIGN_TYPES } from 'shared/constants/campaign';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { CAMPAIGN_STATUS, deriveCampaignStatus } from './campaignStatus';

export const CAMPAIGN_CHANNEL_FILTERS = {
  ALL: 'all',
  LIVE_CHAT: 'live_chat',
  SMS: 'sms',
  WHATSAPP: 'whatsapp',
};

/**
 * The three real channels, in sidebar/chip order. `ALL` is the unscoped view
 * and never appears here.
 */
export const CAMPAIGN_CHANNELS = [
  CAMPAIGN_CHANNEL_FILTERS.LIVE_CHAT,
  CAMPAIGN_CHANNEL_FILTERS.SMS,
  CAMPAIGN_CHANNEL_FILTERS.WHATSAPP,
];

/**
 * Dot colors for the sidebar channel entries. They follow the same channel
 * identity as `helper/channelTone` (website violet, WhatsApp teal, SMS slate)
 * so a channel reads the same in the sidebar as it does on a card.
 */
export const CAMPAIGN_CHANNEL_DOT_CLASSES = {
  [CAMPAIGN_CHANNEL_FILTERS.LIVE_CHAT]: 'bg-n-violet-9',
  [CAMPAIGN_CHANNEL_FILTERS.SMS]: 'bg-n-slate-9',
  [CAMPAIGN_CHANNEL_FILTERS.WHATSAPP]: 'bg-n-teal-9',
};

export const CAMPAIGN_STATUS_FILTERS = {
  ALL: 'all',
  LIVE: 'live',
  SCHEDULED: 'scheduled',
  PAUSED: 'paused',
  DRAFT: 'draft',
  COMPLETED: 'completed',
};

const SMS_CHANNEL_TYPES = [INBOX_TYPES.SMS, INBOX_TYPES.TWILIO];

export const channelForCampaign = campaign => {
  const channelType = campaign.inbox?.channel_type;

  if (campaign.campaign_type === CAMPAIGN_TYPES.ONGOING) {
    return channelType === INBOX_TYPES.WEB
      ? CAMPAIGN_CHANNEL_FILTERS.LIVE_CHAT
      : null;
  }

  if (SMS_CHANNEL_TYPES.includes(channelType)) {
    return CAMPAIGN_CHANNEL_FILTERS.SMS;
  }

  if (channelType === INBOX_TYPES.WHATSAPP) {
    return CAMPAIGN_CHANNEL_FILTERS.WHATSAPP;
  }

  return null;
};

/**
 * How many campaigns each sidebar entry stands for. `all` counts every record,
 * including any whose inbox no longer classifies into a channel, so the
 * sidebar never claims fewer campaigns than the All view actually lists.
 *
 * @returns {object} keyed by `CAMPAIGN_CHANNEL_FILTERS` value.
 */
export const campaignChannelCounts = (campaigns = []) => {
  const counts = {
    [CAMPAIGN_CHANNEL_FILTERS.ALL]: campaigns.length,
    [CAMPAIGN_CHANNEL_FILTERS.LIVE_CHAT]: 0,
    [CAMPAIGN_CHANNEL_FILTERS.SMS]: 0,
    [CAMPAIGN_CHANNEL_FILTERS.WHATSAPP]: 0,
  };

  campaigns.forEach(campaign => {
    const channel = channelForCampaign(campaign);
    if (channel) counts[channel] += 1;
  });

  return counts;
};

/**
 * The `:channel` route segment of the dashboard's channel views. Anything the
 * router hands over that is not a real channel falls back to the All view
 * rather than showing an empty grid for a name nobody can select.
 */
export const channelFromRouteParam = channel =>
  CAMPAIGN_CHANNELS.includes(channel) ? channel : CAMPAIGN_CHANNEL_FILTERS.ALL;

const matchesStatus = (campaign, status) => {
  if (status === CAMPAIGN_STATUS_FILTERS.ALL) return true;

  const derivedStatus = deriveCampaignStatus({
    campaignStatus: campaign.campaign_status,
    campaignType: campaign.campaign_type,
    enabled: campaign.enabled,
    scheduledAt: campaign.scheduled_at,
  });

  if (status === CAMPAIGN_STATUS_FILTERS.LIVE) {
    return [CAMPAIGN_STATUS.ACTIVE, CAMPAIGN_STATUS.PROCESSING].includes(
      derivedStatus
    );
  }

  return derivedStatus === status;
};

const matchesSearch = (campaign, searchQuery) => {
  const query = searchQuery.trim().toLowerCase();
  if (!query) return true;

  return (campaign.title || '').toLowerCase().includes(query);
};

export const filterCampaigns = (
  campaigns,
  {
    channel = CAMPAIGN_CHANNEL_FILTERS.ALL,
    status = CAMPAIGN_STATUS_FILTERS.ALL,
    searchQuery = '',
  } = {}
) =>
  campaigns.filter(
    campaign =>
      (channel === CAMPAIGN_CHANNEL_FILTERS.ALL ||
        channelForCampaign(campaign) === channel) &&
      matchesStatus(campaign, status) &&
      matchesSearch(campaign, searchQuery)
  );
